import { useCallback, useState, useRef, useMemo } from "react";
import {
    ReactFlow,
    Background,
    Controls,
    MiniMap,
    addEdge,
    useNodesState,
    useEdgesState,
    useReactFlow,
    type Connection,
    type Node,
    type Edge,
    type OnConnectStart,
    ReactFlowProvider,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Square, Diamond, Circle, Trash2, Palette } from "lucide-react";
import { HexColorPicker } from "react-colorful";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { flowchartNodeTypes } from "./FlowchartNodes";
import {
    parseMermaidToReactFlow,
    reactFlowToMermaid,
    COLOR_PALETTE,
    type FlowchartNodeData,
    type FlowchartNodeType,
} from "@/lib/mermaid-reactflow-converter";

interface FlowchartEditorModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    initialMermaid?: string;
    onSave: (mermaidSource: string) => void;
}

let nodeIdCounter = 0;

function generateNodeId(): string {
    return `N${++nodeIdCounter}`;
}

function FlowchartEditorInner({
    initialMermaid,
    onSave,
    onCancel,
}: {
    initialMermaid?: string;
    onSave: (src: string) => void;
    onCancel: () => void;
}) {
    const initial = useMemo(() => {
        if (initialMermaid?.trim()) {
            const parsed = parseMermaidToReactFlow(initialMermaid);
            for (const n of parsed.nodes) {
                const num = parseInt(n.id.replace(/\D/g, ""), 10);
                if (!isNaN(num) && num >= nodeIdCounter) nodeIdCounter = num;
            }
            return parsed;
        }
        const startId = generateNodeId();
        return {
            nodes: [{
                id: startId,
                type: "process" as const,
                position: { x: 200, y: 200 },
                data: { label: "Process", nodeType: "process" as const },
            }] as Node<FlowchartNodeData>[],
            edges: [] as Edge[],
        };
    }, [initialMermaid]);

    const [nodes, setNodes, onNodesChange] = useNodesState(initial.nodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(initial.edges);
    const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
    const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);

    const selectedNode = nodes.find((n) => n.id === selectedNodeId) ?? null;
    const selectedEdge = edges.find((e) => e.id === selectedEdgeId) ?? null;

    const edgeLabelRef = useRef<HTMLInputElement>(null);

    const connectStartRef = useRef<{ nodeId: string; handleId: string | null; handleType: string | null } | null>(null);
    const { screenToFlowPosition } = useReactFlow();

    const onConnectStart: OnConnectStart = useCallback((_event, params) => {
        connectStartRef.current = {
            nodeId: params.nodeId ?? "",
            handleId: params.handleId ?? null,
            handleType: params.handleType ?? null,
        };
    }, []);

    const onConnect = useCallback(
        (connection: Connection) => {
            connectStartRef.current = null;
            setEdges((eds) => addEdge({ ...connection, type: "smoothstep" }, eds));
        },
        [setEdges],
    );

    const onConnectEnd = useCallback(
        (event: MouseEvent | TouchEvent) => {
            const start = connectStartRef.current;
            connectStartRef.current = null;
            if (!start?.nodeId) return;

            const target = (event as MouseEvent).target as HTMLElement;
            if (target?.closest(".react-flow__handle")) return;
            if (target?.closest(".react-flow__node")) return;

            const clientX = "changedTouches" in event ? event.changedTouches[0].clientX : (event as MouseEvent).clientX;
            const clientY = "changedTouches" in event ? event.changedTouches[0].clientY : (event as MouseEvent).clientY;
            const position = screenToFlowPosition({ x: clientX, y: clientY });

            const newId = generateNodeId();
            const newNode: Node<FlowchartNodeData> = {
                id: newId,
                type: "process",
                position,
                data: { label: "Process", nodeType: "process" },
            };

            const isSource = start.handleType === "source";
            const newEdge: Edge = {
                id: `e-${start.nodeId}-${newId}`,
                source: isSource ? start.nodeId : newId,
                target: isSource ? newId : start.nodeId,
                sourceHandle: isSource ? start.handleId : null,
                type: "smoothstep",
            };

            setNodes((nds) => [...nds, { ...newNode, selected: true }]);
            setEdges((eds) => [...eds, newEdge]);
            setSelectedNodeId(newId);
            setSelectedEdgeId(null);
        },
        [screenToFlowPosition, setNodes, setEdges],
    );

    const onSelectionChange = useCallback(
        ({ nodes: selNodes, edges: selEdges }: { nodes: Node[]; edges: Edge[] }) => {
            setSelectedNodeId(selNodes.length === 1 ? selNodes[0].id : null);
            setSelectedEdgeId(selEdges.length === 1 ? selEdges[0].id : null);
        },
        [],
    );

    const addNode = useCallback(
        (type: FlowchartNodeType) => {
            const id = generateNodeId();
            const label =
                type === "process" ? "Process" : type === "decision" ? "Decision" : "Terminal";
            const newNode: Node<FlowchartNodeData> = {
                id,
                type,
                position: { x: 200, y: 200 },
                data: { label, nodeType: type },
            };
            setNodes((nds) => [...nds, newNode]);
        },
        [setNodes],
    );

    const deleteSelected = useCallback(() => {
        if (selectedNodeId) {
            setNodes((nds) => nds.filter((n) => n.id !== selectedNodeId));
            setEdges((eds) =>
                eds.filter((e) => e.source !== selectedNodeId && e.target !== selectedNodeId),
            );
            setSelectedNodeId(null);
        }
        if (selectedEdgeId) {
            setEdges((eds) => eds.filter((e) => e.id !== selectedEdgeId));
            setSelectedEdgeId(null);
        }
    }, [selectedNodeId, selectedEdgeId, setNodes, setEdges]);

    const updateNodeLabel = useCallback(
        (label: string) => {
            if (!selectedNodeId) return;
            setNodes((nds) =>
                nds.map((n) =>
                    n.id === selectedNodeId ? { ...n, data: { ...n.data, label } } : n,
                ),
            );
        },
        [selectedNodeId, setNodes],
    );

    const updateNodeColor = useCallback(
        (color: string | undefined) => {
            if (!selectedNodeId) return;
            setNodes((nds) =>
                nds.map((n) =>
                    n.id === selectedNodeId ? { ...n, data: { ...n.data, color } } : n,
                ),
            );
        },
        [selectedNodeId, setNodes],
    );

    const changeNodeType = useCallback(
        (newType: FlowchartNodeType) => {
            if (!selectedNodeId) return;
            setNodes((nds) =>
                nds.map((n) =>
                    n.id === selectedNodeId
                        ? { ...n, type: newType, data: { ...n.data, nodeType: newType } }
                        : n,
                ),
            );
        },
        [selectedNodeId, setNodes],
    );

    const updateEdgeLabel = useCallback(
        (label: string) => {
            if (!selectedEdgeId) return;
            setEdges((eds) =>
                eds.map((e) => (e.id === selectedEdgeId ? { ...e, label } : e)),
            );
        },
        [selectedEdgeId, setEdges],
    );

    const handleSave = useCallback(() => {
        const source = reactFlowToMermaid(
            nodes as Node<FlowchartNodeData>[],
            edges,
        );
        onSave(source);
    }, [nodes, edges, onSave]);

    return (
        <div className="flex h-full">
            {/* Left sidebar */}
            <div className="w-60 border-r bg-muted/30 flex flex-col p-3 gap-3 overflow-y-auto shrink-0">
                <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">
                        Add Node
                    </p>
                    <div className="flex flex-col gap-1.5">
                        <Button
                            variant="outline"
                            size="sm"
                            className="justify-start gap-2"
                            onClick={() => addNode("process")}
                        >
                            <Square className="h-3.5 w-3.5" /> Process
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            className="justify-start gap-2"
                            onClick={() => addNode("decision")}
                        >
                            <Diamond className="h-3.5 w-3.5" /> Decision
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            className="justify-start gap-2"
                            onClick={() => addNode("terminal")}
                        >
                            <Circle className="h-3.5 w-3.5" /> Terminal
                        </Button>
                    </div>
                </div>

                <Separator />

                {selectedNode && (
                    <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            Node Properties
                        </p>
                        <label className="text-xs text-muted-foreground">Label</label>
                        <Input
                            value={(selectedNode.data as FlowchartNodeData).label}
                            onChange={(e) => updateNodeLabel(e.target.value)}
                            className="h-8 text-sm"
                        />
                        <label className="text-xs text-muted-foreground">Type</label>
                        <div className="flex gap-1">
                            {(["process", "decision", "terminal"] as const).map((t) => (
                                <Button
                                    key={t}
                                    variant={selectedNode.type === t ? "default" : "outline"}
                                    size="sm"
                                    className="flex-1 text-xs capitalize"
                                    onClick={() => changeNodeType(t)}
                                >
                                    {t}
                                </Button>
                            ))}
                        </div>
                        <label className="text-xs text-muted-foreground">Color</label>
                        <div className="grid grid-cols-8 gap-1">
                            {COLOR_PALETTE.map(({ bg }) => (
                                <button
                                    key={bg}
                                    className={`w-5 h-5 rounded border cursor-pointer transition-transform hover:scale-125
                                        ${(selectedNode.data as FlowchartNodeData).color === bg ? "ring-2 ring-primary ring-offset-1" : "border-border"}`}
                                    style={{ backgroundColor: bg }}
                                    onClick={() => updateNodeColor(bg)}
                                    title={bg}
                                />
                            ))}
                        </div>
                        <div className="flex gap-1.5 items-center">
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex-1 gap-2 justify-start h-7 text-xs"
                                    >
                                        <Palette className="h-3 w-3" />
                                        Custom
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-3" side="right" align="start">
                                    <HexColorPicker
                                        color={(selectedNode.data as FlowchartNodeData).color || "#ffffff"}
                                        onChange={(c) => updateNodeColor(c)}
                                    />
                                </PopoverContent>
                            </Popover>
                            {(selectedNode.data as FlowchartNodeData).color && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-xs"
                                    onClick={() => updateNodeColor(undefined)}
                                >
                                    Reset
                                </Button>
                            )}
                        </div>
                        <Button
                            variant="destructive"
                            size="sm"
                            className="w-full gap-1.5"
                            onClick={deleteSelected}
                        >
                            <Trash2 className="h-3.5 w-3.5" /> Delete Node
                        </Button>
                    </div>
                )}

                {selectedEdge && (
                    <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            Edge Properties
                        </p>
                        <label className="text-xs text-muted-foreground">Label</label>
                        <Input
                            ref={edgeLabelRef}
                            value={(selectedEdge.label as string) ?? ""}
                            onChange={(e) => updateEdgeLabel(e.target.value)}
                            className="h-8 text-sm"
                            placeholder="Optional label"
                        />
                        <Button
                            variant="destructive"
                            size="sm"
                            className="w-full gap-1.5"
                            onClick={deleteSelected}
                        >
                            <Trash2 className="h-3.5 w-3.5" /> Delete Edge
                        </Button>
                    </div>
                )}

                {!selectedNode && !selectedEdge && (
                    <p className="text-xs text-muted-foreground">
                        Drag from a handle to empty space to add a connected node. Select a node to
                        edit its label, type, or color.
                    </p>
                )}
            </div>

            {/* React Flow canvas */}
            <div className="flex-1 flex flex-col min-w-0">
                <div className="flex-1 min-h-0">
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnectStart={onConnectStart}
                        onConnect={onConnect}
                        onConnectEnd={onConnectEnd}
                        onSelectionChange={onSelectionChange}
                        nodeTypes={flowchartNodeTypes}
                        fitView
                        deleteKeyCode="Delete"
                        className="bg-background"
                    >
                        <Background gap={16} size={1} />
                        <Controls />
                        <MiniMap
                            nodeStrokeWidth={3}
                            className="!bg-muted/50"
                        />
                    </ReactFlow>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-2 p-3 border-t">
                    <Button variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave}>Save Diagram</Button>
                </div>
            </div>
        </div>
    );
}

export default function FlowchartEditorModal({
    open,
    onOpenChange,
    initialMermaid,
    onSave,
}: FlowchartEditorModalProps) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] p-0 flex flex-col gap-0 overflow-hidden">
                <DialogHeader className="px-4 py-3 border-b shrink-0">
                    <DialogTitle>Flowchart Editor</DialogTitle>
                    <DialogDescription>
                        Drag from a handle to empty space to create a connected node. Drag between
                        handles to connect existing nodes. Double-click labels to edit.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 min-h-0">
                    {open && (
                        <ReactFlowProvider>
                            <FlowchartEditorInner
                                initialMermaid={initialMermaid}
                                onSave={(src) => {
                                    onSave(src);
                                    onOpenChange(false);
                                }}
                                onCancel={() => onOpenChange(false)}
                            />
                        </ReactFlowProvider>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
